import type { ChipTone } from '../components/chip';
import type { M365MapSource, M365SyncOutcome, M365Tenant } from '@techgitter/contracts';

/**
 * Display vocabulary for the customer ↔ M365 tenant map (requirements §11.5).
 *
 * Kept out of the page so the labels and the "needs attention" rule are testable
 * on their own — the latter decides what an operator sees by default, and is the
 * difference between 14 actionable rows and 131 undifferentiated ones.
 */

export const MAP_SOURCE_LABELS: Record<M365MapSource, string> = {
  native_domain: 'Native domain',
  managed_domain: 'Managed domain',
  email_domain: 'Email domain',
  manual: 'Manual',
  unmatched: 'Unmatched',
};

/**
 * Tone by match strength, not by mood. A native domain is recorded by an
 * operator and cannot be transferred between tenants; a managed domain is
 * inferred from what TechSolvers registers; an email domain is a hint only —
 * which is why it is amber rather than blue.
 */
export const MAP_SOURCE_TONES: Record<M365MapSource, ChipTone> = {
  native_domain: 'success',
  managed_domain: 'info',
  email_domain: 'warning',
  manual: 'info',
  unmatched: 'neutral',
};

export const OUTCOME_LABELS: Record<M365SyncOutcome, string> = {
  success: 'Success',
  partial: 'Partial',
  failed: 'Failed',
  unmapped: 'Unmapped',
  skipped: 'Skipped',
};

export const OUTCOME_TONES: Record<M365SyncOutcome, ChipTone> = {
  success: 'success',
  partial: 'warning',
  failed: 'error',
  unmapped: 'neutral',
  skipped: 'neutral',
};

/**
 * Does this tenant need a human decision?
 *
 * Two cases, and they are different problems:
 *
 *   - **unmatched** — no customer at all. The tenant syncs nothing, and its
 *     users are attributed to nobody.
 *   - **email_domain** — matched, but on the weakest tier. A customer's contact
 *     address only hints at which tenant they own, so these are the auto-matches
 *     most likely to be wrong.
 *
 * A mapping a human has already confirmed is settled, whatever tier reached it —
 * the reconciler will not revisit it and neither should the default view.
 */
export function needsAttention(tenant: M365Tenant): boolean {
  if (tenant.mapConfirmed) return false;
  return tenant.mapSource === 'unmatched' || tenant.mapSource === 'email_domain';
}

/** Case-insensitive match across the fields an operator would search by. */
export function matchesSearch(tenant: M365Tenant, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    tenant.displayName,
    tenant.customerName,
    tenant.defaultDomain,
    tenant.nativeDomain,
    tenant.tenantId,
    ...(tenant.domains ?? []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

/** `2026-09-18T04:00:00Z` → `18/09/2026`. Empty dash for nothing. */
export function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}
